import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetOrders } from '../../../api/orderApi';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { DEFAULT_ORDER_STATUS_SEGMENT } from '../../../utils/orderStatusFilter';
import type { OrderFilters, UseOrdersReturn } from '../../../types';

const PAGE_SIZE = 12;
// 与用户列表页的关键词防抖同值（`pages/users/page.tsx` 内联了同一个 300ms），改一处时另一处要跟着看
const KEYWORD_DEBOUNCE_MS = 300;

const DEFAULT_FILTERS: OrderFilters = {
  keyword: '',
  // 默认视图是「在途」而不是「全部」：开页先看手上要跑的单一叠，历史单要点「全部」才铺开
  orderStatus: DEFAULT_ORDER_STATUS_SEGMENT,
  paymentStatus: '',
  dateFrom: '',
  dateTo: '',
  serviceType: '',
  // 客户维度只从「客户档案页 → 查看全部」这条路进来（?customer=），筛选面板上没有控件
  customerId: '',
  upcoming: false,
};

/** 只有关键词在动（其余筛选项一字未改）——这是唯一值得等一等的改动。 */
function isKeywordOnly(prev: OrderFilters, next: OrderFilters): boolean {
  return (
    prev.keyword !== next.keyword &&
    JSON.stringify({ ...prev, keyword: '' }) === JSON.stringify({ ...next, keyword: '' })
  );
}

export function useOrders(): UseOrdersReturn {
  const [page, setPage] = useState(1);
  const [filters, setFiltersState] = useState<OrderFilters>(DEFAULT_FILTERS);
  const [refreshTick, setRefreshTick] = useState(0);
  // 真正出网的筛选。整个 filters 是 react-query 查询键的一部分，逐字进键就是逐字一次 GET /orders，
  // 所以这里只让关键词错峰，UI 侧回显与 `hasActiveFilter` 仍读原 `filters`。
  const [queryFilters, setQueryFilters] = useState<OrderFilters>(DEFAULT_FILTERS);

  const typedRef = useRef(filters);
  typedRef.current = filters;

  const setFilters = useCallback((next: OrderFilters) => {
    setFiltersState(next);
    setPage(1);
    // 除关键词外的任何改动都立刻出网，而且必须带上输入框里已经敲下的字：
    // 否则「边打字边点分段」会先发一条旧关键词（甚至空关键词）的请求，列表先闪一下不相干的结果。
    if (!isKeywordOnly(typedRef.current, next)) setQueryFilters(next);
  }, []);

  const debouncedKeyword = useDebouncedValue(filters.keyword, KEYWORD_DEBOUNCE_MS);
  useEffect(() => {
    setQueryFilters(q => (q.keyword === debouncedKeyword ? q : { ...q, keyword: debouncedKeyword }));
  }, [debouncedKeyword]);

  const refresh = useCallback(() => setRefreshTick(t => t + 1), []);

  const { data, isLoading, error } = useGetOrders(queryFilters, page, PAGE_SIZE, refreshTick);

  return {
    orders: data?.data || [],
    total: data?.pagination?.total || 0,
    loading: isLoading,
    error: error?.message || null,
    page,
    pageSize: PAGE_SIZE,
    filters,
    setPage,
    setFilters,
    refresh
  };
}
